// ============================================
// FILE: src/components/PropertyCard.jsx
// ============================================
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, BedDouble, Bath, Ruler, Images, Sparkles } from 'lucide-react';

const CARD_STYLES = `
  .pc-card {
    background: var(--white, #fff);
    border: 1.5px solid var(--border, #e8e0d4);
    border-radius: 20px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
    display: flex; flex-direction: column;
    height: 100%;
  }
  .pc-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 22px 46px rgba(26,18,8,0.12);
    border-color: rgba(200,96,42,0.35);
  }

  .pc-img-wrap { position: relative; width: 100%; height: 220px; overflow: hidden; }
  .pc-img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s ease; display: block; }
  .pc-card:hover .pc-img { transform: scale(1.08); }

  .pc-overlay {
    position: absolute; inset: 0;
    background: linear-gradient(180deg, rgba(20,14,6,0) 40%, rgba(20,14,6,0.55) 100%);
    opacity: 0; transition: opacity 0.3s ease;
    display: flex; align-items: flex-end; justify-content: center; padding-bottom: 18px;
  }
  .pc-card:hover .pc-overlay { opacity: 1; }
  .pc-view-btn {
    background: rgba(255,255,255,0.95); color: var(--ink, #1a1208);
    border: none; border-radius: 999px; padding: 9px 20px;
    font-family: 'Outfit', sans-serif; font-weight: 700; font-size: 13px;
    display: inline-flex; align-items: center; gap: 6px;
    transform: translateY(10px); opacity: 0;
    transition: transform 0.3s ease 0.05s, opacity 0.3s ease 0.05s;
  }
  .pc-card:hover .pc-view-btn { transform: translateY(0); opacity: 1; }

  .pc-badge {
    position: absolute; top: 14px; left: 14px;
    background: var(--terra, #c8602a); color: #fff;
    font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
    padding: 5px 12px; border-radius: 999px;
  }
  .pc-featured {
    position: absolute; top: 14px; right: 14px;
    background: rgba(255,255,255,0.92); color: var(--terra, #c8602a);
    font-size: 11px; font-weight: 700; padding: 5px 10px; border-radius: 999px;
    display: inline-flex; align-items: center; gap: 4px;
  }
  .pc-photo-count {
    position: absolute; bottom: 12px; right: 12px;
    background: rgba(20,14,6,0.6); color: #fff; font-size: 11.5px; font-weight: 600;
    padding: 4px 10px; border-radius: 999px; display: inline-flex; align-items: center; gap: 5px;
  }

  .pc-body { padding: 18px 20px 20px; display: flex; flex-direction: column; flex: 1; }
  .pc-location { display: flex; align-items: center; gap: 5px; color: var(--muted, #8a7968); font-size: 12.5px; margin-bottom: 6px; }
  .pc-title {
    font-family: 'Playfair Display', serif; font-size: 18px; color: var(--ink, #1a1208);
    margin: 0 0 10px; line-height: 1.3;
  }
  .pc-price { font-family: 'Playfair Display', serif; font-size: 20px; color: var(--terra, #c8602a); margin: 0 0 14px; }

  .pc-specs { display: flex; align-items: center; gap: 14px; padding-top: 14px; border-top: 1px solid var(--border, #e8e0d4); margin-top: auto; }
  .pc-spec { display: flex; align-items: center; gap: 5px; font-size: 12.5px; color: var(--muted, #8a7968); font-weight: 500; }
  .pc-spec svg { color: var(--terra, #c8602a); }
`;

const PropertyCard = ({ property }) => {
  const navigate = useNavigate();
  const photoCount = property.images?.length || 1;

  return (
    <>
      <style>{CARD_STYLES}</style>
      <div
        className="pc-card"
        onClick={() => navigate(`/property/${property.id}`)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/property/${property.id}`); }}
      >
        <div className="pc-img-wrap">
          <img className="pc-img" src={property.image} alt={property.title} loading="lazy" />
          <span className="pc-badge">{property.type === 'Rent' ? 'For Rent' : 'For Sale'}</span>
          {property.featured && (
            <span className="pc-featured"><Sparkles size={11} /> Featured</span>
          )}
          <div className="pc-overlay">
            <span className="pc-view-btn"><Images size={14} /> View Full Details</span>
          </div>
          <span className="pc-photo-count"><Images size={12} /> {photoCount}</span>
        </div>

        <div className="pc-body">
          <div className="pc-location"><MapPin size={12} color="var(--terra, #c8602a)" /> {property.location}</div>
          <h3 className="pc-title">{property.title}</h3>
          <p className="pc-price">{property.price}</p>

          <div className="pc-specs">
            {property.bedrooms > 0 && (
              <span className="pc-spec"><BedDouble size={14} /> {property.bedrooms} Bed</span>
            )}
            <span className="pc-spec"><Bath size={14} /> {property.bathrooms} Bath</span>
            <span className="pc-spec"><Ruler size={14} /> {property.area}</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default PropertyCard;